import { ReactNode, useState } from "react";
import { Button } from "@/components/ui/button";
import { ReviewModal } from "@/lib/builder/modals/ReviewModal";
import { Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { useI18n } from "@/hooks/useI18n";

interface BuilderLayoutProps {
  children: ReactNode;
  onPreview?: () => void;
  onSave?: () => void;
  onSaveDraft?: () => void;
  onCancel?: () => void;
  onExit?: () => void;
  onViewTripRequest?: () => void;
  onRejectItinerary?: (note?: string) => void;
  onAcceptItinerary?: (note?: string) => void;
  onUpdateItineraryNote?: (note: string) => Promise<void>;
  showHeader?: boolean;
  showFooter?: boolean;
  onPreviousStep?: () => void;
  onNextStep?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  isRecapStep?: boolean;
  canProceed?: boolean;
  isReadOnly?: boolean;
  isLoading?: boolean;
  isAdmin?: boolean;
}

const BuilderLayout = ({
  children,
  onSaveDraft,
  onSave,
  onCancel,
  onExit,
  onViewTripRequest,
  onRejectItinerary,
  onAcceptItinerary,
  onUpdateItineraryNote,
  showHeader = true,
  showFooter = true,
  onPreviousStep,
  onNextStep,
  isFirstStep = false,
  isLastStep = false,
  isRecapStep = false,
  canProceed = true,
  isReadOnly = false,
  isLoading = false,
  isAdmin = false
}: BuilderLayoutProps) => {
  // State per la modale di review
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const { t } = useI18n();

  const handleSaveDraft = async () => {
    if (!onSaveDraft) return;
    try {
      setIsSavingDraft(true);
      await Promise.resolve(onSaveDraft());
    } catch (err) {
      console.error('Error during save draft', err);
    } finally {
      setIsSavingDraft(false);
    }
  };

  return (
    <div className="h-screen bg-white flex flex-col">
      {/* Header */}
      {showHeader && (
        <header className="flex-shrink-0 border-b border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">

            <div className="flex items-center gap-3">
              {/* Logo */}
              <div className="flex items-center gap-3">
                <img
                  src="/assets/images/dark_small.svg"
                  alt="Place2Be Logo"
                  className="h-8 w-8"
                />
                <h1 className="text-md font-semibold text-gray-900">Itinerary Builder</h1>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={onViewTripRequest} className="flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  View trip request
                </Button>
              </div>
            </div>

            {/* Action Buttons - Only show if not read-only and not loading */}
            {!isReadOnly && !isLoading && (
              <div className="flex items-center gap-4">
                {onSaveDraft && (
                  <Button onClick={handleSaveDraft} variant="outline" size="sm" className="flex items-center gap-2" disabled={isSavingDraft}>
                    {isSavingDraft ? 'Saving...' : 'Save & exit'}
                  </Button>
                )}
              </div>
            )}
            {isReadOnly && onExit && (
              <Button size="sm" onClick={onExit} variant="outline" className="flex items-center gap-2">
                Exit
              </Button>
            )}
          </div>
        </header>
      )}

      {/* Main Content - Scrollable */}
      <main className="flex-1 overflow-y-auto">
        {children}
      </main>

      {/* Footer */}
      {showFooter && (
        <footer className="flex-shrink-0 border-t border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Left side - Back to homepage if first step and onCancel is provided */}
            {!isLoading && (
              <div className="flex items-center gap-2">
                {isFirstStep && onCancel && (
                  <Button variant="ghost" size="sm" onClick={onCancel} className="flex items-center gap-2">
                    <ChevronLeft className="h-4 w-4" />
                    Exit
                  </Button>
                )}
                {onPreviousStep && !isFirstStep && (
                  <Button variant="ghost" size="sm" onClick={onPreviousStep} className="flex items-center gap-2">
                    <ChevronLeft className="h-4 w-4" />
                    back
                  </Button>
                )}
              </div>
            )}

            {/* Right side - Next, Save, or Exit - Only show if not loading */}
            {!isLoading && (
              <div className="flex items-center gap-2">
                {isRecapStep ? (
                  <>
                    {isAdmin && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => setShowReviewModal(true)}
                          className="flex items-center"
                        >
                          Complete review
                        </Button>
                        <ReviewModal
                          isOpen={showReviewModal}
                          onClose={() => setShowReviewModal(false)}
                          onAccept={async (note) => {
                            if (onUpdateItineraryNote) await onUpdateItineraryNote(note);
                            if (onAcceptItinerary) onAcceptItinerary(note);
                            onCancel();
                          }}
                          onReject={async (note) => {
                            if (onUpdateItineraryNote) await onUpdateItineraryNote(note);
                            if (onRejectItinerary) onRejectItinerary(note);
                            onCancel();
                          }}
                        />
                      </>
                    )}

                    {!isAdmin && onSave && !isReadOnly && (
                      <Button size="sm" onClick={onSave} className="flex items-center rounded-md">
                        Create trip quote
                      </Button>
                    )}
                  </>
                ) : (
                  onNextStep && (
                    <Button
                      size="sm"
                      onClick={onNextStep}
                      disabled={!canProceed || isSavingDraft}
                      className="flex items-center gap-2"
                    >
                      {isLastStep ? 'Review' : 'Next'}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  )
                )}
              </div>
            )}
          </div>
        </footer>
      )}
      {isSavingDraft && (
        <div className="fixed inset-0 bg-white/60 z-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('itinerary.savingItinerary')}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuilderLayout;
